import React, { useState, useEffect, useCallback } from "react";
import { adminGetToday, adminGetUserMonthly, downloadAdminReport, adminEditAttendance } from "../../../services/attendanceApi";

/**
 * AdminOverview — today's attendance for ALL employees.
 * - Summary count cards (Present / Late / Absent / On Leave / Total) — CLICKABLE
 * - Clicking a card opens a popup listing exactly those members (name + time)
 * - Searchable / filterable employee table below
 * All icons are inline SVG. Times shown in IST.
 */

const IC = {
  present: <svg viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5"/></svg>,
  late: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>,
  absent: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M15 9l-6 6M9 9l6 6"/></svg>,
  leave: <svg viewBox="0 0 24 24"><path d="M3 21V5a2 2 0 0 1 2-2h9l-2 3 2 3H5"/><path d="M3 21h18"/></svg>,
  wfh: <svg viewBox="0 0 24 24"><path d="M3 11l9-8 9 8"/><path d="M5 10v10h14V10"/><path d="M9 20v-6h6v6"/></svg>,
  half: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M12 3v18" /><path d="M12 3a9 9 0 0 1 0 18z" fill="currentColor" stroke="none"/></svg>,
  notin: <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9"/><path d="M8 12h8"/></svg>,
  team: <svg viewBox="0 0 24 24"><circle cx="9" cy="8" r="3"/><path d="M3 20a6 6 0 0 1 12 0"/><path d="M16 6a3 3 0 0 1 0 6M21 20a6 6 0 0 0-4-5.6"/></svg>,
  search: <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>,
  close: <svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18"/></svg>,
};

function AdminOverview() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [query, setQuery] = useState("");
  const [popup, setPopup] = useState(null); // { key, label, list }
  const [empDetail, setEmpDetail] = useState(null); // { emp, loading, data, error }
  const [editRow, setEditRow] = useState(null); // { record, checkIn, checkOut, status }
  const [savingEdit, setSavingEdit] = useState(false);
  const [editErr, setEditErr] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const res = await adminGetToday();
      const data = res && res.data ? res.data : res;
      setRows(Array.isArray(data) ? data : (data.employees || data.records || []));
    } catch (err) {
      setError(err.message || "Could not load today's overview.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const byStatus = (s) => rows.filter((r) => (r.status || "").toUpperCase() === s);
  // WFH is driven by the is_wfh flag (not a status), so count it separately.
  const isWfhRow = (r) => !!(r.is_wfh ?? r.isWfh);
  const wfhRows = () => rows.filter(isWfhRow);
  const counts = {
    present: byStatus("PRESENT").length,
    late: byStatus("LATE").length,
    // Absent counts ONLY status==='ABSENT'. The richer backend statuses
    // (NOT_CHECKED_IN / WEEKEND / HOLIDAY) must not inflate the absent total.
    absent: byStatus("ABSENT").length,
    leave: byStatus("ON_LEAVE").length,
    wfh: wfhRows().length,
    half: byStatus("HALF_DAY").length,
    notIn: byStatus("NOT_CHECKED_IN").length,
    total: rows.length,
  };

  const openPopup = (key, label, statusKey) => {
    // statusKey "WFH" is special — filtered by the is_wfh flag, not status.
    const list =
      statusKey === "ALL" ? rows : statusKey === "WFH" ? wfhRows() : byStatus(statusKey);
    setPopup({ key, label, list });
  };

  const [exporting, setExporting] = useState("");

  // Export a single employee's report (PDF/Excel) from the detail page
  const exportEmployee = async (emp, format) => {
    const uid = emp.userId || emp.user_id;
    if (!uid) return;
    const now = new Date();
    setExporting(format);
    try {
      // Backend streams a real file (binary) — use the blob download helper
      // so the browser saves it directly (no JSON downloadUrl to open).
      await downloadAdminReport(format, {
        userId: uid,
        month: now.getMonth() + 1,
        year: now.getFullYear(),
      });
    } catch (e) {
      // silent — non-blocking
    } finally {
      setExporting("");
    }
  };

  // Click an employee row -> load that employee's monthly attendance
  const openEmployee = async (emp) => {
    const uid = emp.userId || emp.user_id || emp.employeeId || emp.employee_id;
    if (!uid) return;
    const now = new Date();
    setEmpDetail({ emp, loading: true, data: null, error: "" });
    try {
      const res = await adminGetUserMonthly(uid, now.getMonth() + 1, now.getFullYear());
      const data = res && res.data ? res.data : res;
      setEmpDetail({ emp, loading: false, data, error: "" });
    } catch (err) {
      setEmpDetail({ emp, loading: false, data: null, error: err.message || "Could not load employee data." });
    }
  };

  // ── Admin inline edit of a single attendance record ──
  // Build an IST ISO datetime from the record's work date + a HH:MM time.
  const toISO = (date, time) => {
    if (!date || !time) return null;
    const t = time.length === 5 ? `${time}:00` : time;
    return `${date}T${t}+05:30`;
  };

  // Extract HH:MM (IST) from an ISO string for the time inputs.
  const toTimeInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" });
  };

  const openEdit = (record) => {
    setEditErr("");
    setEditRow({
      record,
      checkIn: toTimeInput(record.checkInTime || record.check_in || record.check_in_time),
      checkOut: toTimeInput(record.checkOutTime || record.check_out || record.check_out_time),
      status: (record.status || "PRESENT").toUpperCase(),
    });
  };

  const saveEdit = async () => {
    if (!editRow) return;
    const rec = editRow.record;
    const recordId = rec.id || rec.recordId || rec.record_id || rec.attendanceId || rec.attendance_id;
    if (!recordId) {
      setEditErr("This record has no id — cannot edit.");
      return;
    }
    const workDate = rec.workDate || rec.work_date || rec.date;
    setSavingEdit(true);
    setEditErr("");
    try {
      await adminEditAttendance(recordId, {
        checkInTime: editRow.checkIn ? toISO(workDate, editRow.checkIn) : null,
        checkOutTime: editRow.checkOut ? toISO(workDate, editRow.checkOut) : null,
        status: editRow.status,
      });
      setEditRow(null);
      // Refresh the currently open employee's monthly detail.
      if (empDetail && empDetail.emp) openEmployee(empDetail.emp);
    } catch (err) {
      setEditErr(err.message || "Could not save changes.");
    } finally {
      setSavingEdit(false);
    }
  };

  const filtered = rows.filter((r) => {
    const s = (r.status || "").toUpperCase();
    if (filter !== "ALL" && s !== filter) return false;
    if (query) {
      const name = (r.fullName || r.full_name || r.name || "").toLowerCase();
      if (!name.includes(query.toLowerCase())) return false;
    }
    return true;
  });

  const statusClass = (s) => {
    switch ((s || "").toUpperCase()) {
      case "PRESENT": return "atl-pill atl-pill--present";
      case "LATE": return "atl-pill atl-pill--late";
      case "ABSENT": return "atl-pill atl-pill--absent";
      case "HALF_DAY": return "atl-pill atl-pill--half";
      // Richer backend statuses — no dedicated colour yet, use the neutral pill.
      case "WFH": return "atl-pill";
      case "ON_LEAVE": return "atl-pill";
      case "WEEKEND": return "atl-pill";
      case "HOLIDAY": return "atl-pill";
      case "NOT_CHECKED_IN": return "atl-pill";
      default: return "atl-pill";
    }
  };

  // Human-friendly label for a backend status code (used in badges).
  const statusLabel = (s) => {
    const map = {
      PRESENT: "Present", LATE: "Late", ABSENT: "Absent", HALF_DAY: "Half-day",
      ON_LEAVE: "On Leave", WFH: "WFH", WEEKEND: "Weekend", HOLIDAY: "Holiday",
      NOT_CHECKED_IN: "Not checked in",
    };
    const key = (s || "").toUpperCase();
    return map[key] || s || "—";
  };

  const nameOf = (r) => r.fullName || r.full_name || r.name || "Employee";

  // ── Full-screen individual employee detail PAGE (not a popup) ──
  if (empDetail) {
    const data = empDetail.data || {};
    const summary = data.summary || data;
    const list = Array.isArray(data) ? data : (data.records || data.days || []);
    const g = (k, alt) => summary[k] ?? summary[alt] ?? 0;
    const emp = empDetail.emp;
    const empId = emp.employeeId || emp.employee_id || "";
    const dept = emp.department || "";
    const totalHrs = g("totalHours", "total_hours");
    const eff = summary.efficiency ?? summary.efficiency_pct ?? null;
    return (
      <div className="atl-emp-page">
        {/* Top action bar */}
        <div className="atl-emp-top">
          <button className="atl-emp-topback" onClick={() => setEmpDetail(null)}>
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
            <span>Back to Overview</span>
          </button>
          <button className="atl-emp-close" onClick={() => setEmpDetail(null)} aria-label="Close">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        {/* Hero header */}
        <div className="atl-emp-hero">
          <div className="atl-emp-hero__id">
            <span className="atl-emp-hero__avatar">{nameOf(emp).charAt(0).toUpperCase()}</span>
            <div className="atl-emp-hero__meta">
              <h2>{nameOf(emp)}</h2>
              <div className="atl-emp-hero__tags">
                {empId && <span className="atl-emp-tag">{empId}</span>}
                {dept && <span className="atl-emp-tag">{dept}</span>}
                <span className="atl-emp-tag atl-emp-tag--soft">This Month</span>
              </div>
            </div>
          </div>
          <div className="atl-emp-hero__actions">
            <button className="atl-btn atl-btn--secondary atl-btn--sm" onClick={() => exportEmployee(emp, "pdf")} disabled={!!exporting}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M12 12v6M9 15l3 3 3-3" /></svg>
              {exporting === "pdf" ? "Exporting..." : "PDF"}
            </button>
            <button className="atl-btn atl-btn--primary atl-btn--sm" onClick={() => exportEmployee(emp, "excel")} disabled={!!exporting}>
              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M9 13l6 6M15 13l-6 6" /></svg>
              {exporting === "excel" ? "Exporting..." : "Excel"}
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="atl-emp-page__body">
          {empDetail.loading ? (
            <div className="atl-skeleton atl-skeleton--tall" />
          ) : empDetail.error ? (
            <div className="atl-error">{empDetail.error}</div>
          ) : (
            <>
              <div className="atl-emp-stats">
                <div className="atl-estat atl-estat--present">
                  <span className="atl-estat__val">{g("present", "present_days")}</span>
                  <span className="atl-estat__lbl">Present</span>
                </div>
                <div className="atl-estat atl-estat--late">
                  <span className="atl-estat__val">{g("late", "late_days")}</span>
                  <span className="atl-estat__lbl">Late</span>
                </div>
                <div className="atl-estat atl-estat--absent">
                  <span className="atl-estat__val">{g("absent", "absent_days")}</span>
                  <span className="atl-estat__lbl">Absent</span>
                </div>
                <div className="atl-estat">
                  <span className="atl-estat__val">{totalHrs || 0}</span>
                  <span className="atl-estat__lbl">Total Hours</span>
                </div>
                {eff !== null && (
                  <div className="atl-estat">
                    <span className="atl-estat__val">{eff}%</span>
                    <span className="atl-estat__lbl">Efficiency</span>
                  </div>
                )}
              </div>

              <div className="atl-card atl-emp-records">
                <h3>Daily Records</h3>
                {list.length === 0 ? (
                  <div className="atl-empty-state">
                    <svg viewBox="0 0 64 64" width="64" height="64" fill="none" stroke="#C3CCD8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="10" y="14" width="44" height="42" rx="4" /><path d="M10 26h44M22 8v10M42 8v10" stroke="#2ABACC" /></svg>
                    <p>No attendance records this month.</p>
                  </div>
                ) : (
                  <div className="atl-table-wrap">
                    <table className="atl-table">
                      <thead><tr><th>Date</th><th>Check In</th><th>Check Out</th><th>Hours</th><th>Status</th><th>Edit</th></tr></thead>
                      <tbody>
                        {list.map((r, i) => (
                          <tr key={i}>
                            <td data-label="Date">{r.workDate || r.work_date || r.date}</td>
                            <td data-label="Check In">{fmt(r.checkInTime || r.check_in || r.check_in_time)}</td>
                            <td data-label="Check Out">{fmt(r.checkOutTime || r.check_out || r.check_out_time)}</td>
                            <td data-label="Hours">{r.totalHours || r.total_hours || "—"}</td>
                            <td data-label="Status"><span className={statusClass(r.status)}>{statusLabel(r.status)}</span></td>
                            <td data-label="Edit">
                              <button className="atl-btn atl-btn--secondary atl-btn--sm" onClick={() => openEdit(r)}>Edit</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Edit attendance modal */}
        {editRow && (
          <div className="atl-popup" onClick={() => !savingEdit && setEditRow(null)}>
            <div className="atl-popup__box" onClick={(e) => e.stopPropagation()}>
              <div className="atl-popup__head">
                <strong>Edit Attendance</strong>
                <button onClick={() => !savingEdit && setEditRow(null)}>{IC.close}</button>
              </div>
              {editErr && <div className="atl-error">{editErr}</div>}
              <div className="atl-row">
                <div className="atl-field">
                  <label>Check In</label>
                  <input type="time" className="atl-input" value={editRow.checkIn} onChange={(e) => setEditRow((v) => ({ ...v, checkIn: e.target.value }))} />
                </div>
                <div className="atl-field">
                  <label>Check Out</label>
                  <input type="time" className="atl-input" value={editRow.checkOut} onChange={(e) => setEditRow((v) => ({ ...v, checkOut: e.target.value }))} />
                </div>
              </div>
              <div className="atl-field">
                <label>Status</label>
                <select className="atl-input" value={editRow.status} onChange={(e) => setEditRow((v) => ({ ...v, status: e.target.value }))}>
                  <option value="PRESENT">Present</option>
                  <option value="LATE">Late</option>
                  <option value="HALF_DAY">Half Day</option>
                  <option value="ABSENT">Absent</option>
                  <option value="ON_LEAVE">On Leave</option>
                </select>
              </div>
              <div className="atl-confirm__actions">
                <button className="atl-btn atl-btn--secondary atl-btn--sm" onClick={() => setEditRow(null)} disabled={savingEdit}>Cancel</button>
                <button className="atl-btn atl-btn--primary atl-btn--sm" onClick={saveEdit} disabled={savingEdit}>{savingEdit ? "Saving..." : "Save"}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

    return (
    <div className="atl-admin-overview">
      {/* Clickable summary cards */}
      <div className="atl-admin-cards">
        <button className="atl-stat atl-stat--present atl-stat--btn" onClick={() => openPopup("present", "Present Members", "PRESENT")}>
          <span className="atl-stat__ic">{IC.present}</span>
          <span className="atl-stat__val">{counts.present}</span><span className="atl-stat__label">Present</span>
        </button>
        <button className="atl-stat atl-stat--late atl-stat--btn" onClick={() => openPopup("late", "Late Members", "LATE")}>
          <span className="atl-stat__ic">{IC.late}</span>
          <span className="atl-stat__val">{counts.late}</span><span className="atl-stat__label">Late</span>
        </button>
        <button className="atl-stat atl-stat--absent atl-stat--btn" onClick={() => openPopup("absent", "Absent Members", "ABSENT")}>
          <span className="atl-stat__ic">{IC.absent}</span>
          <span className="atl-stat__val">{counts.absent}</span><span className="atl-stat__label">Absent</span>
        </button>
        <button className="atl-stat atl-stat--btn" onClick={() => openPopup("leave", "On Leave", "ON_LEAVE")}>
          <span className="atl-stat__ic">{IC.leave}</span>
          <span className="atl-stat__val">{counts.leave}</span><span className="atl-stat__label">On Leave</span>
        </button>
        <button className="atl-stat atl-stat--btn" onClick={() => openPopup("wfh", "Working From Home", "WFH")}>
          <span className="atl-stat__ic">{IC.wfh}</span>
          <span className="atl-stat__val">{counts.wfh}</span><span className="atl-stat__label">WFH</span>
        </button>
        <button className="atl-stat atl-stat--btn" onClick={() => openPopup("half", "Half-day", "HALF_DAY")}>
          <span className="atl-stat__ic">{IC.half}</span>
          <span className="atl-stat__val">{counts.half}</span><span className="atl-stat__label">Half-day</span>
        </button>
        <button className="atl-stat atl-stat--btn" onClick={() => openPopup("notin", "Not Checked In", "NOT_CHECKED_IN")}>
          <span className="atl-stat__ic">{IC.notin}</span>
          <span className="atl-stat__val">{counts.notIn}</span><span className="atl-stat__label">Not checked in</span>
        </button>
        <button className="atl-stat atl-stat--btn" onClick={() => openPopup("total", "All Employees", "ALL")}>
          <span className="atl-stat__ic">{IC.team}</span>
          <span className="atl-stat__val">{counts.total}</span><span className="atl-stat__label">Total</span>
        </button>
      </div>

      <div className="atl-admin-toolbar">
        <div className="atl-search">
          <span className="atl-search__ic">{IC.search}</span>
          <input className="atl-input" placeholder="Search employee..." value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <select className="atl-input" value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="ALL">All Status</option>
          <option value="PRESENT">Present</option>
          <option value="LATE">Late</option>
          <option value="ABSENT">Absent</option>
          <option value="ON_LEAVE">On Leave</option>
        </select>
      </div>

      {error && <div className="atl-error">{error}</div>}
      {loading ? (
        <div className="atl-skeleton atl-skeleton--tall" />
      ) : filtered.length === 0 ? (
        <div className="atl-empty">No employees match.</div>
      ) : (
        <div className="atl-table-wrap">
          <table className="atl-table">
            <thead>
              <tr><th>Employee</th><th>Emp ID</th><th>Check In</th><th>Check Out</th><th>Status</th></tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr key={r.userId || r.user_id || i} className="atl-row-click" onClick={() => openEmployee(r)} title="Click to view monthly detail">
                  <td data-label="Employee">{nameOf(r)}</td>
                  <td data-label="Emp ID">{r.employeeId || r.employee_id || "—"}</td>
                  <td data-label="Check In">{fmt(r.checkInTime || r.check_in || r.check_in_time)}</td>
                  <td data-label="Check Out">{fmt(r.checkOutTime || r.check_out || r.check_out_time)}</td>
                  <td data-label="Status">
                    <span className={statusClass(r.status)}>{statusLabel(r.status)}</span>
                    {(r.is_wfh ?? r.isWfh) && (r.status || "").toUpperCase() !== "WFH" && (
                      <span className="atl-pill" style={{ marginLeft: 6 }}>WFH</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Members popup */}
      {popup && (
        <div className="atl-popup" onClick={() => setPopup(null)}>
          <div className="atl-popup__box atl-popup__box--list" onClick={(e) => e.stopPropagation()}>
            <div className="atl-popup__head">
              <strong>{popup.label} ({popup.list.length})</strong>
              <button onClick={() => setPopup(null)}>{IC.close}</button>
            </div>
            {popup.list.length === 0 ? (
              <div className="atl-empty">No members in this list.</div>
            ) : (
              <ul className="atl-memberlist">
                {popup.list.map((m, i) => (
                  <li key={m.userId || m.user_id || i}>
                    <div className="atl-member">
                      <span className="atl-member__avatar">{nameOf(m).charAt(0).toUpperCase()}</span>
                      <div className="atl-member__info">
                        <strong>{nameOf(m)}</strong>
                        <span>{m.employeeId || m.employee_id || ""}{m.department ? " · " + m.department : ""}</span>
                      </div>
                    </div>
                    <span className="atl-member__time">
                      {fmt(m.checkInTime || m.check_in || m.check_in_time) !== "—" ? "In " + fmt(m.checkInTime || m.check_in || m.check_in_time) : (m.status || "")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

    </div>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" });
}

export default AdminOverview;
